import { Field, YesNoToggle, TextArea, SectionHeader } from "./Field";
import { ShieldCheck, Check } from "lucide-react";

export default function StepBackground({ form, update }) {
  return (
    <div>
      <SectionHeader
        eyebrow="Chapter V"
        title="Background & certification."
        description="Candor is appreciated. Your honesty helps us process applications thoughtfully."
      />

      <div className="space-y-8">
        <Field
          label="Have you or any of the tenants been convicted of a felony in the past 7 years?"
          required
          testid="field-felony"
        >
          <YesNoToggle
            value={form.convicted_felony_7yrs}
            onChange={(v) =>
              update({
                convicted_felony_7yrs: v,
                felony_explanation: v ? form.felony_explanation : "",
              })
            }
            testid="toggle-felony"
          />
        </Field>

        {form.convicted_felony_7yrs && (
          <Field
            label="Please explain"
            hint="A brief explanation of the circumstances."
            testid="field-felony-explanation"
          >
            <TextArea
              value={form.felony_explanation}
              onChange={(v) => update({ felony_explanation: v })}
              placeholder="Explain the circumstances…"
              rows={5}
              testid="input-felony-explanation"
            />
          </Field>
        )}

        <Field
          label="Have you ever been asked to leave or vacate a property while on the lease?"
          required
          testid="field-evicted"
        >
          <YesNoToggle
            value={form.evicted_or_asked_to_vacate}
            onChange={(v) =>
              update({
                evicted_or_asked_to_vacate: v,
                eviction_explanation: v ? form.eviction_explanation : "",
              })
            }
            testid="toggle-evicted"
          />
        </Field>

        {form.evicted_or_asked_to_vacate && (
          <Field
            label="Please explain"
            hint="A brief explanation is sufficient."
            testid="field-eviction-explanation"
          >
            <TextArea
              value={form.eviction_explanation}
              onChange={(v) => update({ eviction_explanation: v })}
              placeholder="Explain the circumstances…"
              rows={5}
              testid="input-eviction-explanation"
            />
          </Field>
        )}

        {/* Disclaimer */}
        <div
          className="mt-4 rounded-sm border border-[#DDD1BC] bg-white p-6"
          data-testid="disclaimer-box"
        >
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck className="w-4 h-4 text-[#A67C2E]" />
            <p className="label-eyebrow text-[#A67C2E]">Notice to Applicants</p>
          </div>
          <p
            className="font-heading text-lg md:text-xl text-[#2A2521] mb-3 tracking-wide"
            data-testid="eo-housing-caps"
          >
            WE ARE AN EQUAL OPPORTUNITY HOUSING PROVIDER.
          </p>
          <p className="text-xs text-[#6B6560] leading-relaxed">
            <strong className="text-[#3A342E]">Experian Properties</strong> does not
            discriminate on the basis of race, color, religion, national origin, sex,
            familial status, disability, or any other class protected by applicable
            federal, state, or local law. All information provided in this application
            is held in <strong className="text-[#3A342E]">strict confidence</strong>{" "}
            and used solely for the purpose of evaluating your application.
          </p>
        </div>

        {/* Certification checkbox */}
        <div
          onClick={() =>
            update({ certification_agreed: !form.certification_agreed })
          }
          role="checkbox"
          aria-checked={form.certification_agreed}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === " " || e.key === "Enter") {
              e.preventDefault();
              update({ certification_agreed: !form.certification_agreed });
            }
          }}
          data-testid="certification-checkbox"
          className={`mt-2 cursor-pointer rounded-sm border p-6 transition ${
            form.certification_agreed
              ? "border-[#A67C2E] bg-[#A67C2E]/[0.08]"
              : "border-[#DDD1BC] bg-white hover:border-[#A67C2E]/60"
          }`}
        >
          <div className="flex items-start gap-4">
            <div
              className={`mt-0.5 w-5 h-5 shrink-0 border rounded-sm flex items-center justify-center transition ${
                form.certification_agreed
                  ? "bg-[#A67C2E] border-[#A67C2E]"
                  : "border-[#C9BC9F] bg-white"
              }`}
            >
              {form.certification_agreed && (
                <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
              )}
            </div>
            <div>
              <p className="label-eyebrow text-[#A67C2E] mb-2">Certification</p>
              <p className="text-sm text-[#3A342E] leading-relaxed">
                I certify that all information provided in this application is{" "}
                <strong>true, complete, and accurate</strong> to the best of my
                knowledge. I authorize Experian Properties to{" "}
                <strong>verify my background</strong>, including credit,
                employment, and rental history, and to{" "}
                <strong>contact the personal references</strong> listed above.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
